/**
 * Per-competitor data used by /migrate/vs/:slug pages.
 *
 * Sources for pricing (Feb 2026):
 *   - migratemycrm.com (SyncMatters public pricing calculator)
 *   - import2.com/pricing
 *   - trujay.com (Data2CRM) public pricing tiers
 *   - slalom.com / publicly reported industry CRM-migration consulting rates
 *
 * Editorial principles (set explicitly with the user):
 *   - Respectful tone — no trashing competitors.
 *   - Position MigrationCrunch as the AI note-recovery winner specifically,
 *     not as a generic "better" tool.
 *   - "When to pick them" section on every page — honesty over hype.
 *   - Public pricing only; flag with "as of Feb 2026" footnote on the page.
 */

export const COMPETITORS = {
  migratemycrm: {
    slug: 'migratemycrm',
    // SEO
    title: 'MigrationCrunch vs MigrateMyCRM (SyncMatters) — Compared Honestly',
    metaDescription:
      'Self-service CRM migration: MigrationCrunch vs MigrateMyCRM (SyncMatters). Pricing, accuracy on freeform notes, supported CRMs, and which one fits your data — compared side by side.',
    // Display
    name: 'MigrateMyCRM',
    parent: 'by SyncMatters',
    domain: 'migratemycrm.com',
    h1Their: 'MigrateMyCRM',
    tagline:
      'Self-service auto-mapping for clean CRM-to-CRM moves across 25+ platforms.',
    // What they're genuinely good at — said out loud, in their voice.
    strengthsHeadline: 'What MigrateMyCRM does well',
    strengths: [
      'Coverage. 25+ source and destination CRMs, more than we support today.',
      'Mature auto-mapping wizard for clean, structured CRM-to-CRM moves.',
      'Trial migration before you pay — same self-service model we use.',
      'Brand reputation in the HubSpot / Salesforce migration space.',
    ],
    // Where they leave value on the table — outcome-only framing.
    gapHeadline: 'Where freeform notes get left behind',
    gaps: [
      'Notes copied as a single "Description" blob — names, phones, and emails inside the text stay buried.',
      'No automatic recovery of contacts or relationships mentioned only inside notes.',
      'Add-ons and cleanup typically billed separately at the higher tiers.',
    ],
    // 5-row "MigrationCrunch tier ↔ MigrateMyCRM tier" mapping.
    tierMap: [
      {
        ours: { name: 'Free Preview', price: '$0', range: '≤ 50 contacts + notes' },
        theirs: { name: '— (no free tier)', price: 'n/a', range: 'Trial migration only' },
        edge: 'You get a real export, free, on up to 50 contacts WITH full AI note extraction. They show you a preview only.',
      },
      {
        ours: { name: 'Contacts Only', price: '$299', range: '≤ 12,500 contacts (no notes)' },
        theirs: { name: 'Starter', price: '$349', range: '< 12,500 records (~800-1,000 contacts + history)' },
        edge:
          'Under $349 sounds cheap until you realize their limit is 12,500 RECORDS — meaning ~800-1,000 contacts with any activity history. Our $299 covers 12,500 CONTACTS regardless of history depth (contacts + fields only). If you have notes, jump to Starter Pro $599 — cheaper than their guided migration add-on alone.',
      },
      {
        ours: { name: 'Starter Pro', price: '$599', range: '≤ 12,500 contacts' },
        theirs: { name: 'Plus', price: '~$299 – $999', range: '≤ ~10k contacts' },
        edge:
          'They start cheaper on paper, but charge separately when freeform notes need cleanup. We extract the entities inside notes at no extra cost.',
      },
      {
        ours: { name: 'Growth AI', price: '$1,799', range: '12,501 – 100,000 contacts' },
        theirs: { name: 'Pro / Custom', price: '~$1,000 – $2,000', range: 'Similar volume' },
        edge:
          'Roughly the same headline price. The differentiator is what happens to the unstructured notes — we auto-extract, they ask you (or a paid expert) to clean them.',
      },
      {
        ours: {
          name: 'Enterprise AI',
          price: '$4,499',
          range: '100,001 – 500,000 contacts',
        },
        theirs: {
          name: 'Premium / Custom Service',
          price: '~$2,199+ (custom)',
          range: 'Custom quote',
        },
        edge:
          'They quote custom for this volume; we publish a flat price. Custom destination + AI Migration Audit PDF included at our tier.',
      },
      {
        ours: { name: 'Unlimited AI', price: '$7,999', range: '500,000+ contacts' },
        theirs: { name: 'Custom Service', price: 'Custom quote', range: '500k+' },
        edge:
          'Flat $7,999 with a dedicated migration engineer and white-glove fulfillment vs an open-ended custom quote.',
      },
    ],
    // "Pick them when" — honesty section
    pickThemWhen: [
      'Your data lives in a CRM we don\'t yet support (e.g. one of the more obscure verticals on their 25+ list).',
      'Your records are already clean — fields populated, notes minimal — and the project is pure CRM-to-CRM mapping.',
      'You already have a working SyncMatters relationship or in-flight migration with them.',
    ],
    // FAQs (each page gets 3-4 unique answers, plus 1 shared "what's the moat")
    faqs: [
      {
        q: 'Is MigrationCrunch cheaper than MigrateMyCRM?',
        a: 'Yes, at the entry tier. Our Contacts Only tier is $299 for up to 12,500 contacts — MigrateMyCRM\'s Starter is $349 and only covers ~800–1,000 contacts once activity history is counted (their limit is 12,500 RECORDS, not contacts). If you also need notes and full history, Starter Pro at $599 beats their Plus tier plus any note-cleanup add-ons they bill separately. AI note extraction is included at every MigrationCrunch paid tier.',
      },
      {
        q: 'Can MigrateMyCRM extract names and phone numbers from inside freeform notes?',
        a: 'Based on their public documentation, no — notes are moved as a single "Description" text blob. Any contact names, phone numbers, or emails inside the note text remain unstructured. That is the specific gap our patent-pending AI is built to solve.',
      },
      {
        q: 'Do both tools support a free trial migration?',
        a: 'MigrateMyCRM offers a preview / trial migration. MigrationCrunch goes a step further with a real, downloadable export for up to 50 contacts + full AI note extraction at no charge — useful if you want to validate the note-extraction quality on your own data before you commit.',
      },
      {
        q: 'What if I\'m already mid-migration with them?',
        a: 'Finish it — switching tools mid-project is usually more pain than benefit. If the result loses the data inside your notes, come back and we will re-run just the notes portion as a Pre-flight Note Recovery job.',
      },
    ],
    // Lindy's plain-English explainer (Feb 2026 market research)
    recordCountingExplainer: {
      headline: 'First, understand how they count your data',
      body: `MigrateMyCRM doesn't charge by contacts. They charge by "records" — and every single item counts separately. 1 contact + 1 email + 1 note + 1 deal = 4 records. A typical Salesforce or HubSpot user with 1,000 contacts has emails, notes, deals, and activity history attached to each one. That easily adds up to 10–15 records per contact. 1,000 contacts = ~12,000–15,000 records in their pricing.`,
      warning: 'You don\'t know which tier you\'re in until you run their estimator tool. 1,100 contacts could cost you $599 — nearly double the $349 headline price.',
      recordExamples: [
        { desc: '1,000 contacts with history', records: '~12,000–15,000 records', theirPrice: '$349 (Starter, under 12,500)' },
        { desc: '1,100 contacts with history', records: '~13,000–16,500 records', theirPrice: '$599 ← jumps here', jump: true },
        { desc: '2,000 contacts with history', records: '~24,000–30,000 records', theirPrice: '$599 (Growth)' },
        { desc: '2,100 contacts with history', records: '~25,000–31,500 records', theirPrice: '$1,199 ← jumps again', jump: true },
        { desc: '7,000 contacts with history', records: '~84,000–105,000 records', theirPrice: '$1,199 (Plus)' },
      ],
    },
    timeToComplete: {
      ours: 'Minutes',
      theirs: 'Hours to days (self-service) · 1-2 weeks (guided +$875) · 3+ weeks (custom)',
      note: 'They offer a "hand-holding" guided migration for $875 EXTRA — and quote 1-2 week delivery. Anything complex is 3+ weeks. If you want a human involved, you\'re waiting 1-3 weeks and paying $875-$1,199+ on top of the base price.',
    },
    bottomLine: 'MigrateMyCRM is a powerful tool built for enterprise IT teams managing complex, multi-system migrations. If you have a 50-person ops team and three months to plan, it\'s a solid option. MigrationCrunch is built for the person who just wants it done. One flat price. Done in minutes. No estimator tool. No surprises.',
  },

  import2: {
    slug: 'import2',
    title: 'MigrationCrunch vs Import2 — CRM Migration Compared',
    metaDescription:
      'Import2 vs MigrationCrunch for CRM migration: pricing, contact limits, what happens to your freeform notes, and which tool is the better fit for small vs note-heavy databases.',
    name: 'Import2',
    parent: '',
    domain: 'import2.com',
    h1Their: 'Import2',
    tagline:
      'Fast, low-cost CRM-to-CRM imports for small teams with clean, simple data.',
    strengthsHeadline: 'What Import2 does well',
    strengths: [
      'Lowest sticker price in the category — great for solo agents and small teams.',
      'Simple, opinionated mapping that works well on clean exports.',
      'Quick — measured in minutes for small databases.',
      'Mature integrations for the most common CRM combinations.',
    ],
    gapHeadline: 'Where the cost shows up',
    gaps: [
      'Notes are typically migrated as raw text only — no entity extraction.',
      'Scaling past a few thousand contacts pushes you into custom-quote territory.',
      'Limited handling of custom objects, secondary contacts, and referral chains.',
    ],
    tierMap: [
      {
        ours: { name: 'Free Preview', price: '$0', range: '≤ 50 contacts + notes' },
        theirs: { name: '— (paid only)', price: 'from ~$99', range: 'No free run' },
        edge: 'Under 50 contacts you can finish a real migration with us for free — notes included.',
      },
      {
        ours: { name: 'Contacts Only', price: '$299', range: '≤ 12,500 contacts (no notes)' },
        theirs: { name: 'Standard base', price: '$499', range: '≤ ~50,000 records (~3,000-3,500 contacts + history)' },
        edge:
          'Import2 charges by records, not contacts. 1,000 contacts with real history usually blows past their $499 base and jumps to $999. Our $299 is a flat $299 for a clean contact list. If you need notes preserved, Starter Pro at $599 still undercuts their higher tier.',
      },
      {
        ours: { name: 'Starter Pro', price: '$599', range: '≤ 12,500 contacts' },
        theirs: { name: 'Standard', price: '$99 – $499', range: '≤ ~2,500 contacts' },
        edge:
          'Import2 wins on price for very small, clean databases. Above ~2,500 contacts or with notes that matter, our Starter Pro covers ~5x the volume and recovers data from inside the notes.',
      },
      {
        ours: { name: 'Growth AI', price: '$1,799', range: '12,501 – 100,000 contacts' },
        theirs: { name: 'Custom', price: 'Custom quote', range: '> ~5k contacts' },
        edge:
          'They typically move to a custom quote here. We publish a flat $1,799 with AI note extraction included.',
      },
      {
        ours: {
          name: 'Enterprise AI',
          price: '$4,499',
          range: '100,001 – 500,000 contacts',
        },
        theirs: { name: 'Custom', price: 'Custom quote', range: 'Custom' },
        edge:
          'Custom on their side, fixed price on ours. AI Migration Audit PDF and custom destination included.',
      },
      {
        ours: { name: 'Unlimited AI', price: '$7,999', range: '500,000+ contacts' },
        theirs: { name: '— (out of comfort zone)', price: 'Custom', range: '500k+' },
        edge:
          'Import2 is genuinely built for small-team simplicity. At half-million-plus contacts you want our white-glove Unlimited tier or one of the boutique consultancies.',
      },
    ],
    pickThemWhen: [
      'You have a few hundred clean contacts, their ~$99 entry price beats our $299 Contacts Only tier, and you don\'t care what\'s buried inside the notes column.',
      'You\'re a solo agent or two-person team moving from one common CRM to another (HubSpot / Pipedrive / Zoho).',
      'Speed and minimum sticker price matter more than note recovery.',
    ],
    faqs: [
      {
        q: 'Is Import2 cheaper than MigrationCrunch?',
        a: 'Only at the very bottom: for a few hundred clean contacts, Import2\'s ~$99 entry price beats our $299 Contacts Only tier. From roughly 1,000 contacts up, their per-record counting usually lands you at $499–$999 while ours stays a flat $299 (contacts only) or $599 (with notes). And once the notes contain phone numbers, names, or emails you don\'t want to lose, MigrationCrunch wins on total cost because it does the work without paid cleanup add-ons.',
      },
      {
        q: 'Does Import2 handle large databases?',
        a: 'Their published pricing tops out long before our Growth AI tier. For 10k+ contacts they typically move to a custom quote. MigrationCrunch publishes a flat one-time fee through 500k contacts and beyond.',
      },
      {
        q: 'Which one is better for Salesforce migrations specifically?',
        a: 'For a Salesforce org with years of typed call notes and activity history, MigrationCrunch is built for exactly that workload — that data is what our patent-pending AI is designed to recover. For a nearly-new Salesforce org with a few hundred clean contacts and no history worth saving, Import2 is a perfectly reasonable choice.',
      },
      {
        q: 'Can I use both?',
        a: 'Yes — and a few customers have. They use Import2 for the cheap structured load, then run a Pre-flight Note Recovery with us on the notes column afterwards. It works, though running just MigrationCrunch end-to-end is simpler.',
      },
    ],
    // Lindy's plain-English explainer (Feb 2026 market research)
    recordCountingExplainer: {
      headline: 'First, understand how they count your data',
      body: `Import2 doesn't charge by the number of contacts. They charge by "records" — and every single item in your database counts as a separate record. 1 contact + 1 note = 2 records. 1 contact + 10 notes + 5 call logs = 16 records. A typical database user with 1,000 contacts has years of notes, call logs, and activity history attached to those contacts. That easily adds up to 10–15 records per contact. 1,000 contacts = ~15,000 records in Import2's pricing.`,
      warning: 'You don\'t know what tier you\'re in until AFTER you run their free sample migration. Then they tell you the price.',
      recordExamples: [
        { desc: '1,000 contacts with history', records: '~15,000 records', theirPrice: '$499' },
        { desc: '3,000 contacts with history', records: '~45,000 records', theirPrice: '$499' },
        { desc: '3,500 contacts with history', records: '~52,500 records', theirPrice: '$999 ← jumps here', jump: true },
        { desc: '6,000 contacts with history', records: '~90,000 records', theirPrice: '$999' },
        { desc: '7,000 contacts with history', records: '~105,000 records', theirPrice: '$1,499 ← jumps again', jump: true },
      ],
    },
    timeToComplete: {
      ours: 'Minutes',
      theirs: 'Hours to days — plus 1-2 business days for support if errors occur',
      note: 'Import2 says "as little as a few hours" — but you run a free sample first (15-30 min) to see the data, THEN manually review field mappings and fix errors, THEN schedule the full migration (several hours to a full day). If there are errors (and there often are), you wait for their support team — 1-2 business days to respond. Total realistic timeline: 1-3 days from start to finish.',
    },
    bottomLine: 'Import2 is built for large enterprise teams migrating between mainstream CRMs like Salesforce and HubSpot. Their pricing model — charging per record — makes sense when you have a 50-person sales team and a dedicated IT department to manage the process. MigrationCrunch is built for people who just want their contacts, notes, and history moved over cleanly, in minutes, at a price that doesn\'t require a spreadsheet to figure out. One flat price. Done in minutes. Every note stays with the right contact.',
  },

  trujay: {
    slug: 'trujay',
    title: 'MigrationCrunch vs Trujay (Data2CRM) — Honest Comparison',
    metaDescription:
      'Trujay (Data2CRM) vs MigrationCrunch: human-verified migrations vs AI note recovery. Pricing, turnaround, accuracy on freeform notes, and how to pick the right tool.',
    name: 'Trujay',
    parent: 'formerly Data2CRM',
    domain: 'trujay.com',
    h1Their: 'Trujay (Data2CRM)',
    tagline:
      'Done-for-you CRM migrations with human verification on every record.',
    strengthsHeadline: 'What Trujay does well',
    strengths: [
      'Human-in-the-loop verification — a real person reviews the data, not just an algorithm.',
      'Strong fit for regulated industries that require manual sign-off on each record.',
      'Mature support team, long track record on Salesforce ↔ HubSpot ↔ Zoho moves.',
      'Custom mapping handled inside the project price rather than as an add-on.',
    ],
    gapHeadline: 'Where the cost and turnaround add up',
    gaps: [
      'Notes-to-fields cleanup is a manual line item — it works, but you\'re paying a human to do what AI now automates.',
      'Turnaround measured in days to weeks, not minutes.',
      'Sales-call required to get a real quote; no flat published pricing for most volumes.',
    ],
    tierMap: [
      {
        ours: { name: 'Free Preview', price: '$0', range: '≤ 50 contacts + notes' },
        theirs: { name: 'Sample / Demo', price: 'Free demo', range: 'Demo only' },
        edge: 'Both offer a free look; ours is a real export, theirs is a sample.',
      },
      {
        ours: { name: 'Starter Pro', price: '$599', range: '≤ 12,500 contacts' },
        theirs: { name: 'Basic project', price: '~$1,500 – $3,000', range: 'Per project' },
        edge:
          'Their base project price typically lands 3-5x higher. Their value at this tier is the human reviewer; ours is the AI note recovery.',
      },
      {
        ours: { name: 'Growth AI', price: '$1,799', range: '12,501 – 100,000 contacts' },
        theirs: { name: 'Mid-tier project', price: '~$3,500 – $5,000', range: 'Per project' },
        edge:
          'Roughly half the price, days faster — and the notes extraction is built in rather than billed as cleanup.',
      },
      {
        ours: {
          name: 'Enterprise AI',
          price: '$4,499',
          range: '100,001 – 500,000 contacts',
        },
        theirs: { name: 'Premium project', price: '~$5,000 – $9,000', range: 'Per project' },
        edge:
          'Close on price at the top of their range; we deliver in minutes vs their multi-week timeline.',
      },
      {
        ours: { name: 'Unlimited AI', price: '$7,999', range: '500,000+ contacts' },
        theirs: { name: 'Custom enterprise', price: 'Custom quote', range: '500k+' },
        edge:
          'Both go custom at the top end. Their fee covers humans; ours covers a dedicated engineer plus the AI pipeline.',
      },
    ],
    pickThemWhen: [
      'Your compliance team requires manual human sign-off on every migrated record (HIPAA, regulated financial services, etc.).',
      'You explicitly want the reassurance of a human reviewer — and the budget supports it.',
      'You\'re in mid-market with custom mapping needs and no time to manage the project yourself.',
    ],
    faqs: [
      {
        q: 'Is MigrationCrunch cheaper than Trujay?',
        a: 'In most public comparisons, yes — roughly half the price at equivalent tiers, sometimes less. Their value-add is the human reviewer; ours is the AI pipeline. Different model, different price.',
      },
      {
        q: 'Why is Trujay so much more expensive?',
        a: 'Their model includes humans verifying records — that\'s labor cost. It\'s a real service, not a markup. It\'s also exactly the line item AI now eats. If you don\'t specifically need a human reviewer, you\'re paying for one.',
      },
      {
        q: 'Which tool is more accurate on freeform notes?',
        a: 'Done well, a careful human reviewer can be very accurate. In practice, manual review of tens of thousands of notes is slow and inconsistent. Our internal benchmarks show 99%+ accuracy on structured + unstructured fields combined, in minutes rather than weeks.',
      },
      {
        q: 'Can I use Trujay\'s human review on top of MigrationCrunch?',
        a: 'Yes — some compliance-heavy customers run the AI extraction first to get the data into structured fields, then have a human reviewer (theirs, ours, or an internal team) verify the high-risk records. We support that workflow.',
      },
    ],
  },

  slalom: {
    slug: 'slalom',
    title: 'MigrationCrunch vs Slalom / Big-Four CRM Consultancies',
    metaDescription:
      'Slalom, Deloitte, Accenture vs MigrationCrunch: when to hire a Fortune-500 CRM consultancy and when a $4,499 AI tool gets the same job done in minutes.',
    name: 'Slalom',
    parent: '(and other Fortune-500 CRM consultancies)',
    domain: 'slalom.com',
    h1Their: 'Slalom / Deloitte / Accenture',
    tagline:
      'Custom-built ETL and change-management projects for Fortune-500 CRM rollouts.',
    strengthsHeadline: 'What the big consultancies do well',
    strengths: [
      'Custom engineering for hundreds of custom objects, complex automations, and integrations.',
      'Change-management and training programs across multi-thousand-seat rollouts.',
      'Regulated-industry experience: financial services, healthcare, pharma, government.',
      'On-site presence, executive stakeholder management, multi-year roadmaps.',
    ],
    gapHeadline: 'Where the pricing gets uncomfortable',
    gaps: [
      'Six-figure minimums — typical CRM migration projects run $25,000 to $150,000+.',
      'Turnaround measured in months to a year for full programs.',
      'Heavy proposal and procurement process before a single record moves.',
    ],
    tierMap: [
      {
        ours: { name: 'Free Preview', price: '$0', range: '≤ 50 contacts + notes' },
        theirs: { name: '— (not their market)', price: 'n/a', range: 'n/a' },
        edge: 'They don\'t take projects this small. We do — for free.',
      },
      {
        ours: { name: 'Starter Pro', price: '$599', range: '≤ 12,500 contacts' },
        theirs: { name: '— (below threshold)', price: 'n/a', range: 'n/a' },
        edge: 'Below their typical project floor.',
      },
      {
        ours: { name: 'Growth AI', price: '$1,799', range: '12,501 – 100,000 contacts' },
        theirs: { name: '— (below threshold)', price: 'n/a', range: 'n/a' },
        edge: 'Still well below their minimum engagement size.',
      },
      {
        ours: {
          name: 'Enterprise AI',
          price: '$4,499',
          range: '100,001 – 500,000 contacts',
        },
        theirs: {
          name: 'Smallest engagement',
          price: '~$25,000 – $50,000',
          range: 'Per project floor',
        },
        edge:
          'At this volume, our flat fee is roughly 5–10x cheaper than their floor. The trade-off: you\'re not also buying change-management consulting.',
      },
      {
        ours: { name: 'Unlimited AI', price: '$7,999', range: '500,000+ contacts' },
        theirs: {
          name: 'Standard enterprise project',
          price: '$75,000 – $150,000+',
          range: 'Per project',
        },
        edge:
          'For the pure data-migration line item, we\'re ~10–20x cheaper. For full Fortune-500 rollout work — custom objects, automations, training — they\'re still the right call.',
      },
    ],
    pickThemWhen: [
      'You\'re Fortune 500 with hundreds of custom objects, complex automations, and a regulated industry.',
      'The CRM migration is one line item inside a multi-year digital-transformation program.',
      'You need executive stakeholder management, on-site teams, and a six-figure budget is already approved.',
    ],
    faqs: [
      {
        q: 'Is MigrationCrunch a real replacement for Slalom or Deloitte?',
        a: 'For the pure data-migration line item — yes, and at a fraction of the cost. For the broader digital-transformation work (change management, custom integrations, training programs across a multi-thousand-seat org), they do things we don\'t do. Use both.',
      },
      {
        q: 'Why do those firms charge so much for a data migration?',
        a: 'Because they bundle it inside a much larger consulting engagement — custom integrations, training, ongoing managed services. The migration is rarely sold standalone. If all you need is the migration, you\'re paying for everything else.',
      },
      {
        q: 'Can I use MigrationCrunch as the data-migration line item inside a Slalom engagement?',
        a: 'Yes — several pilot customers have done exactly that. The consultancy handles the integration and change-management work; we handle the actual data move at a 10x lower cost than the same line item bid internally.',
      },
      {
        q: 'When is this combination NOT a good idea?',
        a: 'When your contract is sole-source and the consultancy can\'t adjust scope, or when compliance requires the migration to be performed by the same vendor doing the integration. Ask your procurement team first.',
      },
    ],
  },
};

export function getCompetitor(slug) {
  if (!slug) return null;
  return COMPETITORS[slug.toLowerCase()] || null;
}

export function listCompetitorSlugs() {
  return Object.keys(COMPETITORS);
}
